import type { ParsedDigitalSignature, PaymentLogFeeFields } from '../types/utils'
/** Signature & Payment tab helpers. */


const isString = (value: unknown): value is string => typeof value === "string"

const looksLikeImageDataUrl = (value: string): boolean =>
  value.trim().toLowerCase().startsWith("data:image")

/** Parse `digital_signature` JSON or raw string/data URL; fallback to `signature`. */
export function parseDigitalSignature(
  rawDigital: unknown,
  fallbackSignature?: unknown,
): ParsedDigitalSignature {
  const fallback = isString(fallbackSignature) ? fallbackSignature : ""

  if (isString(rawDigital) && rawDigital.trim()) {
    try {
      const parsed = JSON.parse(rawDigital) as Record<string, unknown> | null
      if (parsed && typeof parsed === "object") {
        const sig = isString(parsed.signature) ? parsed.signature : ""
        const type = isString(parsed.signaturetype)
          ? parsed.signaturetype
          : isString(parsed.signature_type)
            ? (parsed.signature_type as string)
            : "type1"
        const finalSig = sig || fallback
        return {
          signature: finalSig,
          signatureType: type,
          isImageDataUrl: looksLikeImageDataUrl(finalSig),
        }
      }
    } catch {
      // Not JSON — treat raw value as the signature itself.
    }
    return {
      signature: rawDigital,
      signatureType: "digital",
      isImageDataUrl: looksLikeImageDataUrl(rawDigital),
    }
  }

  return {
    signature: fallback,
    signatureType: fallback ? "digital" : "",
    isImageDataUrl: looksLikeImageDataUrl(fallback),
  }
}

/** Parse currency-ish API values; returns 0 when missing or invalid. */
function toFeeNumber(value: unknown): number {
  if (value === undefined || value === null || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}


/** Base fee column — surcharge/refund adjustments (Angular payment log table). */
export function computePaymentLogBaseFee(payment: PaymentLogFeeFields): number {
  const paymentAmount = toFeeNumber(payment.payment_amount);
  const surcharge = toFeeNumber(payment.payment_log_surcharge_fee ?? payment.surcharge_fee);
  const refundFee = toFeeNumber(payment.payment_log_refund_fee ?? payment.refund_fee);

  if (surcharge > 0 && surcharge < paymentAmount) {
    return paymentAmount - surcharge;
  }
  if (refundFee !== 0) {
    return paymentAmount - refundFee;
  }
  return paymentAmount;
}

/** Service fee — surcharge, else refund fee, else 0 (Angular). */
export function computePaymentLogServiceFee(
  payment: PaymentLogFeeFields,
): number {
  const surcharge = toFeeNumber(payment.payment_log_surcharge_fee ?? payment.surcharge_fee);
  const refundFee = toFeeNumber(payment.payment_log_refund_fee ?? payment.refund_fee);
  if (surcharge !== 0) return surcharge;
  if (refundFee !== 0) return refundFee;
  return 0;
}

/** Transaction cell: DEFAULT type uses auth_code; others use transaction_id. */
export function paymentLogTransactionDisplay(payment: {
  payment_type?: string | null;
  auth_code?: string | number | null;
  transaction_id?: string | number | null;
}): string {
  const type = String(payment.payment_type ?? "").trim();
  if (type === "DEFAULT") {
    const auth = payment.auth_code;
    if (auth !== undefined && auth !== null && String(auth).trim() !== "") {
      return String(auth);
    }
  }
  const tx = payment.transaction_id;
  if (tx !== undefined && tx !== null && String(tx).trim() !== "") {
    return String(tx);
  }
  return "-";
}

function noteText(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

/**
 * Angular Paid By info icon: shown when `transaction_notes` is truthy.
 * Tooltip text:
 * - EXTERNAL_PAYMENT* → `executive_notes` (fallback `transaction_notes`)
 * - otherwise → `transaction_notes`
 */
export function resolvePaymentLogPaidByNotes(payment: {
  payment_source?: string | null;
  transaction_notes?: unknown;
  executive_notes?: unknown;
}): string | null {
  const transactionNotes = noteText(payment.transaction_notes);
  if (!transactionNotes) return null;

  const source = String(payment.payment_source ?? "");
  if (source.includes("EXTERNAL_PAYMENT")) {
    return noteText(payment.executive_notes) || transactionNotes;
  }
  return transactionNotes;
}

const VERIFAST_ID_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In progress",
  complete: "Complete",
  failed: "Failed",
  needs_review: "Needs review",
};

function titleCaseToken(value: string): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** Applicant-level Verifast IDV status for underwriting (read-only). */
export function formatVerifastIdStatus(status: unknown): string {
  if (status === undefined || status === null || status === "") return "";
  const raw = String(status).trim();
  const mapped = VERIFAST_ID_STATUS_LABELS[raw.toLowerCase()];
  if (mapped) return mapped;
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(titleCaseToken)
    .join(" ");
}

/** Verifast acceptance decision for underwriting display. */
export function formatVerifastAcceptanceStatus(status: unknown): string {
  if (status === undefined || status === null || status === "") return "Pending";
  const raw = String(status).trim();
  if (!raw || raw.toUpperCase() === "PENDING") return "Pending";
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(titleCaseToken)
    .join(" ");
}

/** Single-line Verifast status for the application Signature & Payment tab. */
export function formatVerifastUnderwritingLabel(data: {
  verifast_id_status?: unknown;
  verifast_acceptance_status?: unknown;
}): string {
  const idStatus = formatVerifastIdStatus(data.verifast_id_status);
  if (!idStatus) return "";
  const acceptance = formatVerifastAcceptanceStatus(data.verifast_acceptance_status);
  return `${idStatus} · Acceptance: ${acceptance}`;
}
